import React from 'react';
import {strings} from '../../util/strings';
import {getCurrencyCode, redeUnitToSymbol} from '../../util/utility';
import AppSimpleAccordion from '../app-simple-accordion';
import './styles.scss';
import {connect} from 'react-redux';
import {compose} from 'redux';

interface CoverDetailCardProps {
  isFirstLevel?: boolean;
  detail: any;
  deducts: any;
  labels: typeof strings.pt.myCoverages;
  user: any;
}

interface CoverageDetailConditionsProps {
  isFirstLevel?: boolean;
  conditionsType?: string;
  valueRede?: number;
  valueNaoRede?: number;
  unitsRede?: string;
  unitsNaoRede?: string;
  nameRede?: string;
  nameNaoRede?: string;
  nameTypeRede?: string;
  nameTypeNaoRede?: string;
  value?: number;
  currency: string;
  units?: string;
  labels: typeof strings.pt.myCoverages;
  userLanguage: string;
}

interface CoverDetailLevelProps {
  isFirstLevel?: boolean;
  detail: any;
  children?: any;
  deductDataRede?: any;
  deductDataNaoRede?: any;
  labels: typeof strings.pt.myCoverages;
  user: any;
}

const CoverageDetailConditions = ({
  conditionsType,
  valueRede,
  valueNaoRede,
  unitsRede,
  unitsNaoRede,
  nameRede,
  nameNaoRede,
  nameTypeRede,
  nameTypeNaoRede,
  value,
  currency,
  units,
  labels,
  userLanguage,
  isFirstLevel = false,
}: CoverageDetailConditionsProps) => {
  const currencyFormat = (value: number) =>
    new Intl.NumberFormat(userLanguage, {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    }).format(value);
  return (
    <div className="coverage-detail-coditions-wrapper">
      {(value || valueRede || valueNaoRede || nameRede || nameNaoRede) && (
        <div className="coverage-detail-coditions-title-wrapper">
          <p className="coverage-detail-coditions-title">
            {conditionsType === 'copay'
              ? labels?.client_responsibility
              : labels?.deduct}
          </p>
          {value && (
            <p
              className={`coverage-detail-coditions-push-value ${
                isNaN(value) && 'coverage-detail-coditions-push-value--small'
              }`}>
              {isNaN(value) ? value : currencyFormat(value)}
              {!isNaN(value) &&
                (isFirstLevel
                  ? currency == '%'
                    ? currency
                    : getCurrencyCode(currency, 'myCoverages')
                  : getCurrencyCode(currency, 'myCoverages'))}
            </p>
          )}
        </div>
      )}
      <div className="coverage-detail-coditions-content">
        {(valueRede || nameRede) && (
          <AppSimpleAccordion
            header={
              <>
                <p>{labels?.inside_network} </p>
                {valueRede && (
                  <strong className="coverage-detail-coditions-push-value">
                    {currencyFormat(valueRede)}
                    {redeUnitToSymbol(unitsRede, currency)}
                  </strong>
                )}
              </>
            }
            headerClasses="coverage-detail-coditions-header"
            content={
              (nameRede || nameTypeRede) && (
                <div className="coverage-detail-coditions-description">
                  {nameRede && <p>{nameRede}</p>}
                  {nameTypeRede && (
                    <p>
                      {labels?.coverDeductType} <strong>{nameTypeRede}</strong>
                    </p>
                  )}
                </div>
              )
            }
          />
        )}
        {(valueNaoRede || nameNaoRede) && (
          <AppSimpleAccordion
            header={
              <>
                <p>{labels?.outsideNetwork} </p>
                {valueNaoRede && (
                  <strong className="coverage-detail-coditions-push-value">
                    {currencyFormat(valueNaoRede)}
                    {redeUnitToSymbol(unitsNaoRede, currency)}
                  </strong>
                )}
              </>
            }
            headerClasses="coverage-detail-coditions-header"
            content={
              (nameNaoRede || nameTypeNaoRede) && (
                <div className="coverage-detail-coditions-description">
                  {nameNaoRede && (
                    <p className="coverage-detail-coditions-description">
                      {nameNaoRede}
                    </p>
                  )}
                  {nameTypeNaoRede && (
                    <p>
                      {labels?.coverDeductType}{' '}
                      <strong>{nameTypeNaoRede}</strong>
                    </p>
                  )}
                </div>
              )
            }
          />
        )}
      </div>
    </div>
  );
};

const CoverDetailLevel = ({
  isFirstLevel,
  detail,
  children,
  deductDataRede,
  deductDataNaoRede,
  labels,
  user,
}: CoverDetailLevelProps) => {
  const hasContent = !!(
    children ||
    detail?.inNetworkCoverage?.copayment.value ||
    detail?.outsideNetworkCoverage?.copayment.value ||
    detail?.inNetworkCoverage?.copayment.name ||
    detail?.outsideNetworkCoverage?.copayment.name ||
    detail?.inNetworkCoverage?.deductible ||
    detail?.limitCount ||
    detail?.deductAmtRede ||
    detail?.deductNaoRede
  );

  const currencyFormat = (value: number) =>
    new Intl.NumberFormat(user.language, {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    }).format(value);

  return (
    <div className={`detail-level-${isFirstLevel ? '1' : detail.level}-item`}>
      {detail.level === 3 ? (
        <AppSimpleAccordion
          header={
            <>
              <p>{detail.displayName}</p>
              {detail.limitAmt && (
                <p
                  className={`detail-level-${detail.level}-value coverage-detail-coditions-push-value`}>
                  {labels?.until} {currencyFormat(detail.limitAmt)}
                  {getCurrencyCode(detail.currency, 'myCoverages')}
                </p>
              )}
            </>
          }
          headerClasses={`detail-level-${detail.level}-header`}
          content={
            hasContent && (
              <>
                {children}
                {(detail.inNetworkCoverage.copayment.value ||
                  detail.outsideNetworkCoverage.copayment.value ||
                  detail.inNetworkCoverage.copayment.name ||
                  detail.outsideNetworkCoverage.copayment.name) && (
                  <CoverageDetailConditions
                    conditionsType="copay"
                    valueRede={detail.inNetworkCoverage.copayment.value}
                    valueNaoRede={detail.outsideNetworkCoverage.copayment.value}
                    unitsRede={detail.inNetworkCoverage.copayment.units}
                    unitsNaoRede={detail.outsideNetworkCoverage.copayment.units}
                    nameRede={detail.inNetworkCoverage.copayment.name}
                    nameNaoRede={detail.outsideNetworkCoverage.copayment.name}
                    labels={labels}
                    currency={detail.currency}
                    userLanguage={user.language}
                  />
                )}
                {detail.inNetworkCoverage.deductible?.footnote ? (
                  <p className="detail-rodape-rede">
                    {detail.inNetworkCoverage.deductible?.footnote}
                  </p>
                ) : (
                  detail.outsideNetworkCoverage.deductible?.footnote && (
                    <p className="detail-rodape-rede">
                      {detail.outsideNetworkCoverage.deductible?.footnote}
                    </p>
                  )
                )}
                {detail.limitCount && (
                  <div className="detail-limit-count">
                    <p>{labels?.appointment_limit} </p>
                    <p>{detail.limitCount}</p>
                  </div>
                )}
                <CoverageDetailConditions
                  conditionsType="deduct"
                  valueRede={detail.deductAmtRede}
                  valueNaoRede={detail.deductAmtNaoRede}
                  unitsRede={getCurrencyCode(detail.currency, 'myCoverages')}
                  unitsNaoRede={getCurrencyCode(detail.currency, 'myCoverages')}
                  currency={detail.currency}
                  nameRede={
                    deductDataRede
                      ? deductDataRede.deductDetails.find(
                          (detail: any) =>
                            detail.languageAlias === user.language
                        ).longDescription
                      : undefined
                  }
                  nameNaoRede={
                    deductDataNaoRede
                      ? deductDataNaoRede.deductDetails.find(
                          (detail: any) =>
                            detail.languageAlias === user.language
                        ).longDescription
                      : undefined
                  }
                  nameTypeRede={
                    deductDataRede
                      ? deductDataRede.deductDetails.find(
                          (detail: any) =>
                            detail.languageAlias === user.language
                        ).shortDescription
                      : undefined
                  }
                  nameTypeNaoRede={
                    deductDataNaoRede
                      ? deductDataNaoRede.deductDetails.find(
                          (detail: any) =>
                            detail.languageAlias === user.language
                        ).shortDescription
                      : undefined
                  }
                  labels={labels}
                  userLanguage={user.language}
                />
              </>
            )
          }
          contentClasses={`detail-level-${detail.level}-content`}
        />
      ) : (
        <>
          {!isFirstLevel && (
            <div
              className={`detail-level-${
                isFirstLevel ? '1' : detail.level
              }-header`}>
              <p>{detail.displayName}</p>
              {detail.limitAmt && (
                <p
                  className={`detail-level-${
                    isFirstLevel ? '1' : detail.level
                  }-value`}>
                  {currencyFormat(detail.limitAmt)}
                  {getCurrencyCode(detail.currency, 'myCoverages')}
                </p>
              )}
            </div>
          )}
          {children}
          {isFirstLevel && (
            <CoverageDetailConditions
              conditionsType="copay"
              value={detail?.copayValue || detail?.copayName}
              units={detail?.copayUnits}
              labels={labels}
              currency={detail?.copayUnits || detail?.currency}
              userLanguage={user.language}
              isFirstLevel={isFirstLevel}
            />
          )}

          {(detail.inNetworkCoverage?.copayment.value ||
            detail.outsideNetworkCoverage?.copayment.value ||
            detail.inNetworkCoverage?.copayment.name ||
            detail.outsideNetworkCoverage?.copayment.name) && (
            <CoverageDetailConditions
              conditionsType="copay"
              valueRede={detail.inNetworkCoverage?.copayment.value}
              valueNaoRede={detail.outsideNetworkCoverage?.copayment.value}
              unitsRede={detail.inNetworkCoverage?.copayment.units}
              unitsNaoRede={detail.outsideNetworkCoverage?.copayment.units}
              nameRede={detail.inNetworkCoverage?.copayment.name}
              nameNaoRede={detail.outsideNetworkCoverage?.copayment.name}
              labels={labels}
              currency={detail.currency}
              userLanguage={user.language}
            />
          )}
          {detail.inNetworkCoverage?.deductible?.footnote ? (
            <p className="detail-rodape-rede">
              {detail.inNetworkCoverage?.deductible?.footnote}
            </p>
          ) : (
            detail.outsideNetworkCoverage?.deductible?.footnote && (
              <p className="detail-rodape-rede">
                {detail.outsideNetworkCoverage?.deductible?.footnote}
              </p>
            )
          )}
          {detail.limitCount && (
            <div className="detail-limit-count">
              <p>{labels?.appointment_limit} </p>
              <p>{detail.limitCount}</p>
            </div>
          )}
          <CoverageDetailConditions
            conditionsType="deduct"
            valueRede={detail.deductAmtRede}
            valueNaoRede={detail.deductAmtNaoRede}
            unitsRede={getCurrencyCode(detail.currency, 'myCoverages')}
            unitsNaoRede={getCurrencyCode(detail.currency, 'myCoverages')}
            currency={detail.currency}
            nameRede={
              deductDataRede
                ? deductDataRede.deductDetails.find(
                    (detail: any) => detail.languageAlias === user.language
                  ).longDescription
                : undefined
            }
            nameNaoRede={
              deductDataNaoRede
                ? deductDataNaoRede.deductDetails.find(
                    (detail: any) => detail.languageAlias === user.language
                  ).longDescription
                : undefined
            }
            nameTypeRede={
              deductDataRede
                ? deductDataRede.deductDetails.find(
                    (detail: any) => detail.languageAlias === user.language
                  ).shortDescription
                : undefined
            }
            nameTypeNaoRede={
              deductDataNaoRede
                ? deductDataNaoRede.deductDetails.find(
                    (detail: any) => detail.languageAlias === user.language
                  ).shortDescription
                : undefined
            }
            labels={labels}
            userLanguage={user.language}
          />
        </>
      )}
    </div>
  );
};

const CoverDetailCard = ({
  detail,
  deducts,
  labels,
  isFirstLevel,
  user,
}: CoverDetailCardProps) => {
  const deductDataRede = deducts?.filter(
    (item: any) => item.deductType === detail.deductTypeRede
  )[0];
  const deductDataNaoRede = deducts?.filter(
    (item: any) => item.deductType === detail.deductTypeNaoRede
  )[0];
  const haveSubcoverages = isFirstLevel
    ? false
    : detail.subCoverages.length > 0;
  const subCoverages = haveSubcoverages && (
    <div className="nested-cover-detail-wrap">
      {detail.subCoverages.map((subcobertura: any) => (
        <CoverDetailLevel
          detail={subcobertura}
          deductDataRede={deductDataRede}
          deductDataNaoRede={deductDataNaoRede}
          labels={labels}
          user={user}
        />
      ))}
    </div>
  );
  return (
    <div className="cover-detail-card-wrap">
      <CoverDetailLevel
        isFirstLevel={isFirstLevel}
        detail={detail}
        children={subCoverages}
        deductDataRede={deductDataRede}
        deductDataNaoRede={deductDataNaoRede}
        labels={labels}
        user={user}
      />
    </div>
  );
};

const mapStateToProps = ({user, strings}: any) => ({
  user: user.value,
  labels: strings.value.pt.myCoverages,
});

export default compose(connect(mapStateToProps)(CoverDetailCard));
